import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import type { StepperPanelStep } from 'lumen/components/hotel/benchmark-insight/competitors/compset-builder/stepper-panel-step';
import type HotelSettingsCompetitorsBenchmarkCompsetBuilderDesiredCompsetRoute from 'lumen/routes/hotel/settings/competitors/benchmark/compset/builder/desired-compset';
import type { ModelFrom } from 'lumen/utils/routes';

export default class HotelSettingsCompetitorsBenchmarkCompsetBuilderDesiredCompsetController extends Controller {
  declare model: ModelFrom<HotelSettingsCompetitorsBenchmarkCompsetBuilderDesiredCompsetRoute>;

  @service('benchmark-insight/competitors/desired-compset')
  declare benchmarkInsightCompetitorsDesiredCompset: Services['benchmark-insight/competitors/desired-compset'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare usageAnalytics: Services['usage-analytics'];

  MIN_COMPETITORS = 4;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedNumberOfCompetitors() {
    return this.intl.t('benchmark_insight.count_hotels', {
      numberOfHotels:
        this.benchmarkInsightCompetitorsDesiredCompset.desiredCompetitors
          .length,
      maxNumberOfHotels:
        this.benchmarkInsightCompetitorsDesiredCompset.desiredCompset
          ?.MAX_COMPETITORS,
    });
  }

  get isNextDisabled(): boolean {
    if (
      this.benchmarkInsightCompetitorsDesiredCompset.saveDesiredCompsetTask
        .isRunning
    ) {
      return true;
    }

    const { desiredCompset } = this.benchmarkInsightCompetitorsDesiredCompset;

    if (!desiredCompset) {
      return true;
    }

    if (desiredCompset.competitors.length < this.MIN_COMPETITORS) {
      return true;
    }

    return false;
  }

  get showUpdateHeaderTranslation(): boolean {
    if (!this.model.compset.last?.value) {
      return false;
    }

    return this.model.compset.last.value.competitors.length > 0;
  }

  get stepperPanelSteps(): StepperPanelStep[] {
    const { desiredCompset } = this.benchmarkInsightCompetitorsDesiredCompset;

    if (!desiredCompset) {
      return [];
    }

    const steps: StepperPanelStep[] = [];

    if (desiredCompset.hasPrefilledDesiredCompetitors) {
      const desiredCompsetStep = {
        title: this.intl.t(
          'settings.competitors.benchmark.compset.builder.stepper.desired_compset.1.title',
        ),
        description: this.intl.t(
          'settings.competitors.benchmark.compset.builder.stepper.desired_compset.1.description',
        ),
        icon: 'circle-checkmark' as const,
        isComplete: true,
      };

      steps.push(desiredCompsetStep);
    }

    const stepAddHotels = {
      title: this.intl.t(
        'settings.competitors.benchmark.compset.builder.stepper.desired_compset.2.title',
        { count: this.MIN_COMPETITORS },
      ),
      description: this.intl.t(
        'settings.competitors.benchmark.compset.builder.stepper.desired_compset.2.description',
        { count: this.MIN_COMPETITORS },
      ),
      icon: 'circle-checkmark' as const,
      isComplete: desiredCompset.competitors.length >= this.MIN_COMPETITORS,
    };
    steps.push(stepAddHotels);

    steps.push({
      title: this.intl.t(
        'settings.competitors.benchmark.compset.builder.stepper.desired_compset.3.title',
      ),
      description: this.intl.t(
        'settings.competitors.benchmark.compset.builder.stepper.desired_compset.3.description',
      ),
      icon: 'circle-checkmark',
      isComplete: stepAddHotels.isComplete,
      isActive: !stepAddHotels.isComplete,
    });

    steps.forEach((step) => {
      step.isPartOfFlow = true;
    });

    return steps;
  }

  @action onClickSave(): void {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const transitionToGenerateRoute = () => {
      // Use legacyTransitionTo to avoid the parent model refreshing
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.router.legacyTransitionTo(
        'hotel.settings.competitors.benchmark.compset.builder.generate',
      );
    };

    const { desiredCompset } = this.benchmarkInsightCompetitorsDesiredCompset;

    // If there are no changes to the desired compset, there's no need to save.
    // Backend generates the compset proposals based on the currently saved desired compset.
    if (!desiredCompset?.hasChanges) {
      transitionToGenerateRoute();
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.benchmarkInsightCompetitorsDesiredCompset.saveDesiredCompsetTask.perform(
      false,
      transitionToGenerateRoute,
    );

    this.usageAnalytics.track('Benchmark Compset Proposal Viewed', {
      eventTrigger: 'compset-builder',
    });
  }

  @action onClose(): void {
    // Use legacyTransitionTo to avoid the parent model refreshing
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.router.legacyTransitionTo(
      'hotel.settings.competitors.benchmark.compset.index',
    );

    this.benchmarkInsightCompetitorsDesiredCompset.discardChanges();

    this.usageAnalytics.track('Benchmark Compset Cancelled', {
      eventTrigger: 'compset-builder',
    });
  }
}
